import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  CheckCircle2,
  Clock,
  MessageCircle,
  Navigation,
  StickyNote,
  Save,
  X,
  Undo2,
} from "lucide-react";

function formatDuration(seconds) {
  if (!seconds) return "N/A";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
}

// Format time as "10:00pm"
function formatTimeOfDay(date) {
  return date.toLocaleTimeString('en-GB', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  }).toLowerCase();
}

export function StopItem({
  postcode,
  index,
  displayNumber,
  isFirst,
  onComplete,
  onUncomplete,
  onOpenMaps,
  onEditNotes,
  onSaveNotes,
  onCancelNotes,
  editingNotes,
  notesText,
  setNotesText
}) {
  // Handle WhatsApp share with time range
  const handleShareETA = () => {
    if (!postcode.cumulative_eta) return;
    
    const now = new Date();
    const arrivalTime = new Date(now.getTime() + (postcode.cumulative_eta * 1000));
    const arrivalTimePlus30 = new Date(arrivalTime.getTime() + (30 * 60 * 1000));
    
    const timeFrom = formatTimeOfDay(arrivalTime);
    const timeTo = formatTimeOfDay(arrivalTimePlus30);
    
    const message = `Hi, we will be with you around ${timeFrom} - ${timeTo}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`p-4 rounded-xl border mb-3 ${
        postcode.completed
          ? "bg-green-50 border-green-200"
          : isFirst
          ? "bg-blue-50 border-blue-200"
          : "bg-white border-slate-100"
      }`}
      data-testid={`route-item-${postcode.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
              postcode.completed ? "bg-green-500" : "bg-blue-500"
            }`}
          >
            {postcode.completed ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              displayNumber
            )}
          </div>
          <div>
            <p className={`font-semibold ${postcode.completed ? 'text-green-700' : 'text-slate-900'}`}>
              {postcode.name}
            </p>
            <p className="text-sm text-slate-500 font-mono">
              {postcode.postcode}
            </p>
            {postcode.completed ? (
              <p className="text-sm text-green-600 mt-1">
                <CheckCircle2 className="w-3 h-3 inline mr-1" />
                Completed
              </p>
            ) : postcode.cumulative_eta ? (
              <p className="text-sm text-blue-600 mt-1">
                <Clock className="w-3 h-3 inline mr-1" />
                ETA: {formatDuration(postcode.cumulative_eta)}
              </p>
            ) : null}
          </div>
        </div>
        
        {postcode.completed ? (
          <div className="flex gap-1">
            <Button
              data-testid={`notes-btn-${postcode.id}`}
              size="icon"
              variant="ghost"
              className={`h-8 w-8 rounded-full hover:bg-amber-100 ${postcode.notes ? 'text-amber-500' : 'text-slate-400'}`}
              onClick={() => onEditNotes(postcode)}
            >
              <StickyNote className="w-4 h-4" />
            </Button>
            <Button
              data-testid={`undo-btn-${postcode.id}`}
              size="sm"
              variant="outline"
              className="rounded-full h-8 px-3 border-green-300 text-green-700 hover:bg-green-100"
              onClick={() => onUncomplete(postcode.id)}
            >
              <Undo2 className="w-4 h-4 mr-1" />
              Undo
            </Button>
          </div>
        ) : (
          <div className="flex gap-1">
            <Button
              data-testid={`notes-btn-${postcode.id}`}
              size="icon"
              variant="ghost"
              className={`h-8 w-8 rounded-full hover:bg-amber-100 ${postcode.notes ? 'text-amber-500' : 'text-slate-400'}`}
              onClick={() => onEditNotes(postcode)}
            >
              <StickyNote className="w-4 h-4" />
            </Button>
            <Button
              data-testid={`share-btn-${postcode.id}`}
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full hover:bg-green-100"
              onClick={handleShareETA}
              title="Send ETA via WhatsApp"
              disabled={!postcode.cumulative_eta}
            >
              <MessageCircle className="w-4 h-4 text-green-600" />
            </Button>
            <Button
              data-testid={`maps-btn-${postcode.id}`}
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full hover:bg-blue-100"
              onClick={() => onOpenMaps(postcode)}
              title="Navigate with Maps"
            >
              <Navigation className="w-4 h-4 text-blue-500" />
            </Button>
            <Button
              data-testid={`complete-btn-${postcode.id}`}
              size="sm"
              className="rounded-full h-8 px-3 bg-green-500 hover:bg-green-600"
              onClick={() => onComplete(postcode.id)}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Done
            </Button>
          </div>
        )}
      </div>
      
      {/* Notes Section */}
      {editingNotes === postcode.id ? (
        <div className="mt-3 ml-11">
          <Textarea
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            placeholder="Add notes for this stop..."
            className="min-h-[60px] text-sm"
            data-testid={`notes-input-${postcode.id}`}
          />
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              onClick={() => onSaveNotes(postcode.id)}
              className="gap-1 h-8"
              data-testid={`save-notes-${postcode.id}`}
            >
              <Save className="w-3 h-3" />
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancelNotes}
              className="gap-1 h-8"
            >
              <X className="w-3 h-3" />
              Cancel
            </Button>
          </div>
        </div>
      ) : postcode.notes ? (
        <div 
          className="mt-3 ml-11 p-2 bg-amber-50 rounded-lg text-sm text-amber-800 cursor-pointer hover:bg-amber-100"
          onClick={() => onEditNotes(postcode)}
        >
          <StickyNote className="w-3 h-3 inline mr-1" />
          {postcode.notes}
        </div>
      ) : null}
    </motion.div>
  );
}
